/**
 * Persistent house-foil overlay + glow for Phaser card sprites.
 * Matches Vue CardArt (Rare / Super / Ultra / Helios / Nightglass / Stormwake).
 */

import type { CardFoilId } from '@shared/index'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { stampFoilName } from '../utils/foilName'

type FoilOverlayId = Exclude<CardFoilId, 'common'>

const TEX: Record<FoilOverlayId, string> = {
  rare: 'house-foil-rare',
  super: 'house-foil-super',
  ultra: 'house-foil-ultra',
  ghost: 'house-foil-ghost',
  helios: 'house-foil-helios',
  nightglass: 'house-foil-nightglass-v2',
  stormwake: 'house-foil-stormwake-v2',
}

export const FOIL_GLOW: Record<FoilOverlayId, number> = {
  rare: 0xcbd5e1,
  super: 0xfacc15,
  ultra: 0xd97706,
  ghost: 0xe2e8f0,
  helios: 0xfbbf24,
  nightglass: 0x38bdf8,
  stormwake: 0xfb7185,
}

/** Same diagonal highlight CardArt uses (`foil-sweep`, 7.5s). */
const SWEEP_TEX = 'house-foil-sweep'
const SWEEP_LIGHT_TEX = 'house-foil-sweep-light'
const SWEEP_TEX_W = 220
const SWEEP_TEX_H = 92
const SWEEP_MS = 7500
/** Source pixels of the sweep that fill one card. */
const SWEEP_WINDOW = 64
/**
 * Two periods side by side so the light window can slide one full cycle
 * and land on the same pixels. One shared texture, no per-card canvas.
 */
const SWEEP_CANVAS_W = SWEEP_TEX_W * 2
const STORMWAKE_SPARKS = 'house-foil-stormwake-sparks'
const SPARK_FROM = 0.08
const SPARK_TO = 1
const SPARK_HOLD_MS = 90
const SPARK_GAP_MS = 160
const AURA_TEX = 'house-foil-aura'
const AURA_PAD = 8
const AURA_TEX_W = 64 + AURA_PAD * 2
const AURA_TEX_H = 92 + AURA_PAD * 2

const SWEEP_TINT: Record<FoilOverlayId, number> = {
  rare: 0xffffff,
  super: 0xfde68a,
  ultra: 0xfbbf24,
  ghost: 0xf8fafc,
  helios: 0xffe7a3,
  nightglass: 0xbae6fd,
  stormwake: 0xffffff,
}

type StormwakeSpark = {
  image: Phaser.GameObjects.Image
  duration: number
  delay: number
}

type FoilClock = {
  sweeps: Set<Phaser.GameObjects.Image>
  sparks: Set<StormwakeSpark>
}

const foilClocks = new WeakMap<Phaser.Scene, FoilClock>()

const sweepSprites = new WeakMap<
  Phaser.Scene,
  Set<Phaser.GameObjects.TileSprite>
>()

function fillCanvas(
  scene: Phaser.Scene,
  key: string,
  w: number,
  h: number,
  draw: (ctx: CanvasRenderingContext2D) => void,
): void {
  if (scene.textures.exists(key)) return
  const tex = scene.textures.createCanvas(key, w, h)
  if (!tex) return
  draw(tex.context)
  tex.refresh()
}

/**
 * Build shared foil textures once per scene (safe to call repeatedly).
 */
export function ensureRarityFoilTextures(scene: Phaser.Scene): void {
  fillCanvas(scene, TEX.rare, 64, 92, (ctx) => {
    const g = ctx.createLinearGradient(0, 0, 64, 0)
    g.addColorStop(0, 'rgba(226,232,240,0)')
    g.addColorStop(0.5, 'rgba(255,255,255,0.45)')
    g.addColorStop(1, 'rgba(226,232,240,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 64, 92)
  })

  fillCanvas(scene, TEX.super, 64, 92, (ctx) => {
    const g = ctx.createLinearGradient(0, 0, 64, 92)
    g.addColorStop(0, 'rgba(250,204,21,0.38)')
    g.addColorStop(0.5, 'rgba(163,230,53,0.2)')
    g.addColorStop(1, 'rgba(251,146,60,0.32)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 64, 92)
  })

  fillCanvas(scene, TEX.ultra, 64, 92, (ctx) => {
    const g = ctx.createLinearGradient(0, 0, 24, 92)
    g.addColorStop(0, 'rgba(146,64,14,0.7)')
    g.addColorStop(0.35, 'rgba(251,191,36,0.75)')
    g.addColorStop(0.5, 'rgba(255,247,200,0.5)')
    g.addColorStop(1, 'rgba(180,83,9,0.68)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 64, 92)
    for (let x = 2; x < 64; x += 10) {
      ctx.fillStyle = 'rgba(255,215,90,0.28)'
      ctx.fillRect(x, 0, 2, 92)
    }
  })

  fillCanvas(scene, TEX.ghost, 64, 92, (ctx) => {
    const g = ctx.createLinearGradient(0, 0, 0, 92)
    g.addColorStop(0, 'rgba(255,255,255,0.55)')
    g.addColorStop(0.45, 'rgba(226,232,240,0.28)')
    g.addColorStop(1, 'rgba(148,163,184,0.18)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 64, 92)
    for (let y = 4; y < 92; y += 11) {
      ctx.fillStyle = 'rgba(255,255,255,0.22)'
      ctx.fillRect(0, y, 64, 1)
    }
  })

  fillCanvas(scene, TEX.helios, 64, 92, (ctx) => {
    const g = ctx.createRadialGradient(32, 40, 4, 32, 40, 60)
    g.addColorStop(0, 'rgba(255,247,220,0.55)')
    g.addColorStop(0.45, 'rgba(251,191,36,0.4)')
    g.addColorStop(1, 'rgba(180,80,0,0.15)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 64, 92)
    ctx.translate(32, 40)
    for (let i = 0; i < 16; i += 1) {
      ctx.rotate(Math.PI / 8)
      ctx.fillStyle = i % 2 === 0
        ? 'rgba(255,220,80,0.35)'
        : 'rgba(255,255,230,0.18)'
      ctx.fillRect(0, -2, 48, 3)
    }
  })

  fillCanvas(scene, TEX.nightglass, 64, 92, (ctx) => {
    const a = ctx.createRadialGradient(12, 18, 2, 20, 24, 42)
    a.addColorStop(0, 'rgba(186,230,253,0.75)')
    a.addColorStop(1, 'rgba(186,230,253,0)')
    const b = ctx.createRadialGradient(52, 72, 2, 48, 64, 40)
    b.addColorStop(0, 'rgba(167,139,250,0.7)')
    b.addColorStop(1, 'rgba(167,139,250,0)')
    const g = ctx.createLinearGradient(0, 0, 64, 92)
    g.addColorStop(0, 'rgba(56,189,248,0.55)')
    g.addColorStop(0.38, 'rgba(167,139,250,0.5)')
    g.addColorStop(0.62, 'rgba(244,114,182,0.38)')
    g.addColorStop(1, 'rgba(34,211,238,0.55)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 64, 92)
    ctx.fillStyle = a
    ctx.fillRect(0, 0, 64, 92)
    ctx.fillStyle = b
    ctx.fillRect(0, 0, 64, 92)
    ctx.save()
    ctx.rotate(0.35)
    for (let x = -40; x < 120; x += 8) {
      ctx.fillStyle = 'rgba(224,242,254,0.38)'
      ctx.fillRect(x, -40, 2.5, 180)
    }
    ctx.restore()
  })

  fillCanvas(scene, TEX.stormwake, 64, 92, (ctx) => {
    const wash = ctx.createRadialGradient(16, 12, 2, 32, 48, 70)
    wash.addColorStop(0, 'rgba(255,90,180,0.78)')
    wash.addColorStop(0.45, 'rgba(20,230,255,0.5)')
    wash.addColorStop(1, 'rgba(244,114,182,0.22)')
    ctx.fillStyle = wash
    ctx.fillRect(0, 0, 64, 92)
  })

  fillCanvas(scene, STORMWAKE_SPARKS, 64, 92, (ctx) => {
    const sparks: Array<[number, number, string, number]> = [
      [8, 10, '#f9a8d4', 5.5],
      [22, 28, '#67e8f9', 4.8],
      [38, 8, '#ffffff', 6],
      [52, 24, '#f472b6', 5],
      [12, 50, '#a5f3fc', 5.2],
      [30, 62, '#ffffff', 6.2],
      [46, 46, '#f9a8d4', 4.6],
      [56, 68, '#22d3ee', 5.4],
      [6, 78, '#ffffff', 5],
      [24, 86, '#f472b6', 5.6],
      [50, 84, '#67e8f9', 4.8],
      [40, 32, '#ffffff', 4.4],
    ]
    for (const [x, y, color, r] of sparks) {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r)
      g.addColorStop(0, 'rgba(255,255,255,1)')
      g.addColorStop(0.32, color)
      g.addColorStop(1, 'rgba(255,255,255,0)')
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()
    }
  })

  fillCanvas(scene, AURA_TEX, AURA_TEX_W, AURA_TEX_H, (ctx) => {
    ctx.shadowColor = 'rgba(255,255,255,1)'
    ctx.shadowBlur = 14
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    const x = AURA_PAD
    const y = AURA_PAD
    const w = 64
    const h = 92
    const r = 8
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + w, y, x + w, y + h, r)
    ctx.arcTo(x + w, y + h, x, y + h, r)
    ctx.arcTo(x, y + h, x, y, r)
    ctx.arcTo(x, y, x + w, y, r)
    ctx.closePath()
    ctx.fill()
  })

  fillCanvas(scene, SWEEP_TEX, SWEEP_TEX_W, SWEEP_TEX_H, (ctx) => {
    paintSweepBand(ctx, 0)
  })

  fillCanvas(scene, SWEEP_LIGHT_TEX, SWEEP_CANVAS_W, SWEEP_TEX_H, (ctx) => {
    for (let ox = 0; ox < SWEEP_CANVAS_W; ox += SWEEP_TEX_W) {
      paintSweepBand(ctx, ox)
    }
  })
}

function paintSweepBand(ctx: CanvasRenderingContext2D, ox: number): void {
  const g = ctx.createLinearGradient(ox, 0, ox + SWEEP_TEX_W, 36)
  g.addColorStop(0.36, 'rgba(255,255,255,0)')
  g.addColorStop(0.46, 'rgba(226,232,240,0.35)')
  g.addColorStop(0.5, 'rgba(255,255,255,0.92)')
  g.addColorStop(0.54, 'rgba(226,232,240,0.35)')
  g.addColorStop(0.64, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(ox, 0, SWEEP_TEX_W, SWEEP_TEX_H)
}

/**
 * Soft rarity aura behind the card art (not PreFX — that downsamples the face).
 */
function addFoilAura(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  foil: FoilOverlayId,
  cardW: number,
  cardH: number,
): void {
  let alpha = 0.55
  if (foil === 'rare') alpha = 0.4
  if (foil === 'helios' || foil === 'stormwake' || foil === 'nightglass') {
    alpha = 0.7
  }
  const aura = scene.add
    .image(0, 0, AURA_TEX)
    .setDisplaySize(cardW + AURA_PAD * 2, cardH + AURA_PAD * 2)
    .setTint(FOIL_GLOW[foil])
    .setAlpha(alpha)
    .setBlendMode(Phaser.BlendModes.ADD)
  container.addAt(aura, 0)
}

/**
 * Overlay the house foil wash plus the same shine sweep as Vue CardArt.
 */
export function applyCardRarityFx(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  foil: CardFoilId,
  cardW: number,
  cardH: number,
  face?: Phaser.GameObjects.Image,
  cardId?: string,
): void {
  if (foil === 'common') return
  ensureRarityFoilTextures(scene)
  addFoilAura(scene, container, foil, cardW, cardH)
  if (foil !== 'rare') {
    let alpha = 0.42
    if (foil === 'ultra') alpha = 0.62
    if (foil === 'ghost') alpha = 0.4
    if (foil === 'nightglass') alpha = 0.7
    if (foil === 'helios') alpha = 0.5
    if (foil === 'stormwake') alpha = 0.72
    container.add(
      scene.add
        .image(0, 0, TEX[foil])
        .setDisplaySize(cardW, cardH)
        .setAlpha(alpha)
        .setBlendMode(Phaser.BlendModes.ADD),
    )
  }
  let sweepAlpha = 0.8
  if (foil === 'rare') sweepAlpha = 0.95
  else if (foil === 'ultra') sweepAlpha = 0.92
  else if (foil === 'ghost') sweepAlpha = 0.7
  else if (foil === 'nightglass') sweepAlpha = 0.96
  else if (foil === 'stormwake') sweepAlpha = 0.95
  if (useDuelPrefsStore().foilSweep === 'light') {
    addLightSweep(scene, container, cardW, cardH, foil, sweepAlpha)
  } else {
    addFullSweep(scene, container, cardW, cardH, foil, sweepAlpha)
  }
  if (foil === 'stormwake') {
    addStormwakeSparks(scene, container, cardW, cardH, hookFoilClock(scene))
  }
  stampFoilName(scene, container, foil, cardW, cardH, face, cardId)
}

function addFullSweep(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  cardW: number,
  cardH: number,
  foil: FoilOverlayId,
  sweepAlpha: number,
): void {
  const scale = cardW / SWEEP_WINDOW
  const sweep = scene.add.tileSprite(0, 0, cardW, cardH, SWEEP_TEX)
  sweep.setTileScale(scale, cardH / SWEEP_TEX_H)
  sweep.setTint(SWEEP_TINT[foil])
  sweep.setAlpha(sweepAlpha)
  sweep.setBlendMode(Phaser.BlendModes.ADD)
  sweep.setData('foilSweepScale', scale)
  container.add(sweep)
  const tracked = hookSweepTicker(scene)
  tracked.add(sweep)
  sweep.once('destroy', () => tracked.delete(sweep))
}

/**
 * Usual shine: each card scrolls its own tile. This is the look Settings
 * calls Full.
 */
function hookSweepTicker(
  scene: Phaser.Scene,
): Set<Phaser.GameObjects.TileSprite> {
  let set = sweepSprites.get(scene)
  if (set) return set
  set = new Set()
  sweepSprites.set(scene, set)
  const onUpdate = (): void => {
    if (set.size === 0) return
    const u = (scene.time.now % SWEEP_MS) / SWEEP_MS
    for (const sprite of set) {
      if (!sprite.active) {
        set.delete(sprite)
        continue
      }
      const scale = Number(sprite.getData('foilSweepScale') ?? 1)
      sprite.tilePositionX = u * SWEEP_TEX_W * scale
    }
  }
  scene.events.on('update', onUpdate)
  scene.events.once('shutdown', () => {
    scene.events.off('update', onUpdate)
    set.clear()
    sweepSprites.delete(scene)
  })
  return set
}

/** Shared texture. Settings calls this Light. */
function addLightSweep(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  cardW: number,
  cardH: number,
  foil: FoilOverlayId,
  sweepAlpha: number,
): void {
  const sweep = scene.add
    .image(0, 0, SWEEP_LIGHT_TEX)
    .setDisplaySize(cardW, cardH)
    .setTint(SWEEP_TINT[foil])
    .setAlpha(sweepAlpha)
    .setBlendMode(Phaser.BlendModes.ADD)
  sweep.setData('foilCardW', cardW)
  sweep.setData('foilCardH', cardH)
  container.add(sweep)
  const clock = hookFoilClock(scene)
  clock.sweeps.add(sweep)
  scrollSweep(scene, scene.time.now)
  sweep.once('destroy', () => {
    clock.sweeps.delete(sweep)
  })
}

/**
 * One clock per scene for the light sweep and Stormwake sparks.
 * The light sweep slides one shared source window. `setCrop` would shrink
 * each quad, so the sprite scale is what keeps the shine on the whole card.
 */
function hookFoilClock(scene: Phaser.Scene): FoilClock {
  const existing = foilClocks.get(scene)
  if (existing) return existing
  const clock: FoilClock = { sweeps: new Set(), sparks: new Set() }
  foilClocks.set(scene, clock)
  const onUpdate = (): void => {
    if (clock.sweeps.size === 0 && clock.sparks.size === 0) return
    const now = scene.time.now
    if (clock.sweeps.size > 0) scrollSweep(scene, now)
    for (const spark of clock.sparks) {
      if (!spark.image.active) {
        clock.sparks.delete(spark)
        continue
      }
      spark.image.setAlpha(sparkAlpha(now, spark.duration, spark.delay))
    }
  }
  scene.events.on('update', onUpdate)
  scene.events.once('shutdown', () => {
    scene.events.off('update', onUpdate)
    clock.sparks.clear()
    clock.sweeps.clear()
    foilClocks.delete(scene)
  })
  return clock
}

/**
 * Slide a 64px window across one period of the shared sweep texture.
 * Canvas samples `cutX` / `cutWidth`; WebGL samples the UVs `updateUVs`
 * writes from that same window. The quad is sized from `cutWidth`, so each
 * card's scale is put back to its display size.
 */
function scrollSweep(scene: Phaser.Scene, now: number): void {
  if (!scene.textures.exists(SWEEP_LIGHT_TEX)) return
  const frame = scene.textures.get(SWEEP_LIGHT_TEX).get()
  if (!frame) return
  const srcW = frame.source.width
  const srcH = frame.source.height
  if (srcW <= 0 || srcH <= 0) return
  const u = (now % SWEEP_MS) / SWEEP_MS
  const x = u * SWEEP_TEX_W
  frame.cutX = x
  frame.cutY = 0
  frame.cutWidth = SWEEP_WINDOW
  frame.cutHeight = srcH
  frame.updateUVs()
  const draw = frame.canvasData as { x: number; y: number }
  draw.x = x
  draw.y = 0
  const images = foilClocks.get(scene)?.sweeps
  if (!images) return
  for (const image of images) {
    if (!image.active) continue
    const cardW = Number(image.getData('foilCardW'))
    const cardH = Number(image.getData('foilCardH'))
    // Origin follows this size. Leaving it at the full texture width
    // would park the 64px window on the left of the card.
    image.setSize(SWEEP_WINDOW, srcH)
    if (cardW > 0) image.scaleX = cardW / SWEEP_WINDOW
    if (cardH > 0) image.scaleY = cardH / srcH
  }
}

function cubicInOut(v: number): number {
  if ((v *= 2) < 1) return 0.5 * v * v * v
  return 0.5 * ((v -= 2) * v * v + 2)
}

/** Same 0.08–1 twinkle the old yoyo tweens used (hold 90, gap 160). */
function sparkAlpha(now: number, duration: number, delay: number): number {
  const period = duration * 2 + SPARK_HOLD_MS + SPARK_GAP_MS
  let t = (now - delay) % period
  if (t < 0) t += period
  if (t < duration) {
    return SPARK_FROM + (SPARK_TO - SPARK_FROM) * cubicInOut(t / duration)
  }
  t -= duration
  if (t < SPARK_HOLD_MS) return SPARK_TO
  t -= SPARK_HOLD_MS
  if (t < duration) {
    return SPARK_TO + (SPARK_FROM - SPARK_TO) * cubicInOut(t / duration)
  }
  return SPARK_FROM
}

function trackSpark(
  clock: FoilClock,
  image: Phaser.GameObjects.Image,
  duration: number,
  delay: number,
): void {
  const spark: StormwakeSpark = { image, duration, delay }
  clock.sparks.add(spark)
  image.setAlpha(sparkAlpha(image.scene.time.now, duration, delay))
  image.once('destroy', () => clock.sparks.delete(spark))
}

function addStormwakeSparks(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  cardW: number,
  cardH: number,
  clock: FoilClock,
): void {
  const a = scene.add
    .image(0, 0, STORMWAKE_SPARKS)
    .setDisplaySize(cardW, cardH)
    .setBlendMode(Phaser.BlendModes.ADD)
  const b = scene.add
    .image(0, 0, STORMWAKE_SPARKS)
    .setDisplaySize(cardW, cardH)
    .setFlipX(true)
    .setFlipY(true)
    .setBlendMode(Phaser.BlendModes.ADD)
  container.add(a)
  container.add(b)
  trackSpark(clock, a, 280, 0)
  trackSpark(clock, b, 360, 220)
}
